import React, { useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Calendar, Phone } from 'lucide-react';
import { ctaContent, companyInfo } from '../mock';
import { toast } from 'sonner';

const CTASection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact/consultation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Thank you! We will contact you within 24 hours.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
      } else {
        toast.error(data.detail || 'Failed to send your request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting consultation:', error);
      toast.error('An error occurred. Please try again later or contact us directly via email.');
    }
  };

  return (
    <section id="cta" className="relative bg-gray-900 py-32 overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1762427907123-c7ab022a5de7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHw0fHx0YXglMjBkb2N1bWVudHMlMjBjYWxjdWxhdG9yfGVufDB8fHx8MTc3MjI3MTg5MHww&ixlib=rb-4.1.0&q=85"
          alt="Financial Planning"
          className="w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/95 to-gray-900/90"></div>
      </div>

      {/* Background Gradient */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-[#0099FF]/10 rounded-full blur-[200px]"></div>

      <div className="relative max-w-[1400px] mx-auto px-[7.6923%]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Column - Content */}
          <div>
            <div className="inline-block bg-[#0099FF]/10 border border-[#0099FF]/30 px-4 py-2 rounded-none mb-8">
              <span className="text-[#0099FF] text-sm font-medium">GET STARTED TODAY</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-semibold text-white mb-6 tracking-tight leading-tight">
              {ctaContent.headline}
            </h2>
            
            <div className="text-left mb-8">
              <p className="font-heading text-xl font-bold text-white mb-1">ಆರ್ ಎಸ್ ಟಿ ಅಂಡ್ ಕಂ</p>
              <p className="font-heading text-base text-gray-300">ಸನ್ನದು ಲೆಕ್ಕ ಪರಿಶೋಧಕರು</p>
            </div>

            <p className="text-xl text-gray-300 mb-10 leading-relaxed">
              {ctaContent.subheadline}
            </p>

            {/* Quick Contact Options */}
            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#0099FF]/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="text-[#0099FF]" size={20} />
                </div>
                <div>
                  <div className="text-gray-400 text-sm mb-1">Call Us Directly</div>
                  <a href={`tel:${companyInfo.phone}`} className="text-white text-lg font-medium hover:text-[#0099FF] transition-colors duration-300">
                    {companyInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#0099FF]/10 flex items-center justify-center flex-shrink-0">
                  <Calendar className="text-[#0099FF]" size={20} />
                </div>
                <div>
                  <div className="text-gray-400 text-sm mb-1">Email for Consultation</div>
                  <a href="mailto:info@rstandco.co.in?subject=Consultation Request" className="text-white text-lg font-medium hover:text-[#0099FF] transition-colors duration-300">
                    info@rstandco.co.in
                  </a>
                </div>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap gap-6 pt-8 border-t border-gray-700">
              <div className="text-gray-400 text-sm">✓ No Obligation</div>
              <div className="text-gray-400 text-sm">✓ 24-Hour Response Time</div>
              <div className="text-gray-400 text-sm">✓ Confidential Discussion</div>
            </div>
          </div>

          {/* Right Column - Contact Form */}
          <div className="bg-white border border-gray-700 p-8 lg:p-10 shadow-2xl">
            <h3 className="text-2xl font-semibold text-gray-900 mb-8">Schedule Your Consultation</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-gray-700 text-sm mb-2 font-medium">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full bg-white border border-gray-300 text-gray-900 px-4 py-3 focus:outline-none focus:border-[#0099FF] transition-colors duration-300 rounded-none"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-gray-700 text-sm mb-2 font-medium">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full bg-white border border-gray-300 text-gray-900 px-4 py-3 focus:outline-none focus:border-[#0099FF] transition-colors duration-300 rounded-none"
                  placeholder="you@company.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-gray-700 text-sm mb-2 font-medium">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full bg-white border border-gray-300 text-gray-900 px-4 py-3 focus:outline-none focus:border-[#0099FF] transition-colors duration-300 rounded-none"
                  placeholder="+91-XXXXXXXXXX"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-gray-700 text-sm mb-2 font-medium">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full bg-white border border-gray-300 text-gray-900 px-4 py-3 focus:outline-none focus:border-[#0099FF] transition-colors duration-300 rounded-none"
                  placeholder="Your company name"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-gray-700 text-sm mb-2 font-medium">
                  How can we help?
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="4"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="w-full bg-white border border-gray-300 text-gray-900 px-4 py-3 focus:outline-none focus:border-[#0099FF] transition-colors duration-300 rounded-none resize-none"
                  placeholder="Tell us about your needs..."
                ></textarea>
              </div>

              <Button 
                type="submit"
                className="w-full bg-[#0099FF] hover:bg-[#0077CC] text-white font-medium px-8 py-6 text-lg rounded-none transition-all duration-400 group"
              >
                Book My Consultation
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={20} />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
